'use client';

import { useEffect, useState } from 'react';

import { athenaPost } from './athena-fetch';
import { useUserSession } from './user-session';

import { isRecord, stringField } from '@/lib/athena-utils';
import { consumerUrl } from '@/lib/urls';

export type Lead = {
  id: string;
  url: string;
  street: string | undefined;
  readableOfferState: string | undefined;
};

const LIST_LEADS_QUERY = /* GraphQL */ `
  query ListLeadsOnUniqueAddresses($customerUuid: String!) {
    sellDirect {
      listLeadsOnUniqueAddresses(input: { customerUuid: $customerUuid }) {
        leads {
          id
          isQualified
          latestOffer {
            state
          }
          address {
            uuid
            street
          }
        }
      }
    }
  }
`;

// Mirror cosmos useLeads: only the offer states it surfaces to customers.
function readableOfferState(state: string | undefined): string | undefined {
  switch (state) {
    case 'SENT':
    case 'ACCEPTED':
      return 'Offered';
    case 'EXPIRED':
      return 'Expired';
    case 'CONTINGENCY_RELEASED':
    case 'IN_CONTRACT':
      return 'In Contract';
    case 'CLOSED':
      return 'Closed';
    default:
      return undefined;
  }
}

function extractLeads(data: unknown): Lead[] {
  if (!isRecord(data)) return [];
  const sellDirect = data['sellDirect'];
  if (!isRecord(sellDirect)) return [];
  const list = sellDirect['listLeadsOnUniqueAddresses'];
  if (!isRecord(list)) return [];
  const leads = list['leads'];
  if (!Array.isArray(leads)) return [];

  const result: Lead[] = [];
  for (const raw of leads) {
    if (!isRecord(raw)) continue;
    if (raw['isQualified'] !== true) continue;
    const id = stringField(raw, 'id');
    if (!id) continue;
    const address = isRecord(raw['address']) ? raw['address'] : undefined;
    const addressUuid = address ? stringField(address, 'uuid') : undefined;
    if (!addressUuid) continue;
    const latestOffer = isRecord(raw['latestOffer']) ? raw['latestOffer'] : undefined;
    const state = latestOffer ? stringField(latestOffer, 'state') : undefined;
    result.push({
      id,
      url: consumerUrl(`/dashboard/sell/${addressUuid}`),
      street: address ? stringField(address, 'street') : undefined,
      readableOfferState: readableOfferState(state),
    });
  }
  return result;
}

async function fetchLeads(customerUuid: string): Promise<Lead[] | undefined> {
  // No AbortSignal: the inflight promise is shared across mounted consumers
  // (HeaderAccount, MobileNav). Aborting when one unmounts would break the
  // others still waiting; the per-consumer `cancelled` flag below already
  // prevents stale setState.
  const data = await athenaPost<unknown>({
    operationName: 'ListLeadsOnUniqueAddresses',
    query: LIST_LEADS_QUERY,
    variables: { customerUuid },
  });
  if (data === undefined) return undefined;
  return extractLeads(data);
}

// Module-level cache keyed by customerUuid so re-renders and multiple consumers
// share a single fetch per session.
const cache = new Map<string, Lead[]>();
const inflight = new Map<string, Promise<Lead[] | undefined>>();

export function useLeads(): Lead[] {
  const auth = useUserSession();
  const customerUuid = auth.kind === 'authenticated' ? auth.human.customerUuid : undefined;
  const [leads, setLeads] = useState<Lead[]>(() =>
    customerUuid ? (cache.get(customerUuid) ?? []) : [],
  );

  useEffect(() => {
    if (!customerUuid) {
      setLeads([]);
      return;
    }
    const cached = cache.get(customerUuid);
    if (cached) {
      setLeads(cached);
      return;
    }
    let cancelled = false;
    let request = inflight.get(customerUuid);
    if (!request) {
      request = fetchLeads(customerUuid).then((result) => {
        inflight.delete(customerUuid);
        if (result) cache.set(customerUuid, result);
        return result;
      });
      inflight.set(customerUuid, request);
    }
    request.then((result) => {
      if (cancelled) return;
      if (result) setLeads(result);
    });
    return () => {
      cancelled = true;
    };
  }, [customerUuid]);

  return leads;
}
